import { ArrowRightOutlined, PushpinFilled, PushpinOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { Button, Input, Typography } from "../ui";
import { ThemedModal } from "../components/ThemedModal";
import { QeddyIcon } from "../help/QeddyIcon";
import { matchSearchPages, type SearchEntry, type SearchGroup, type SearchPage, type SearchSource } from "./searchContract";
import { searchTranslations, type SearchLabels } from "./searchTranslations";
import "./quickSearch.css";
import { LANGUAGE_DE } from "../i18n/languageCodes";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_ESCAPE } from "../ui/keyboardKeys";

const SEARCH_DEBOUNCE_MS = 200;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_STALE_TIME_MS = 30_000;
const SEARCH_MAX_LENGTH = 200;
const QEDDY_ICON_SIZE = 32;
const QEDDY_ENTRY_KEY = "qeddy";

export type QuickSearchDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Include account, tenant, company/project/studio and effective rights in this identity. */
  scopeKey: string;
  language: string;
  labels?: Partial<SearchLabels>;
  pages: readonly SearchPage[];
  onPageOpen: (page: SearchPage) => void;
  pagePin?: (page: SearchPage) => SearchEntry["pin"];
  source?: SearchSource;
  initialGroups?: SearchGroup[];
  ask?: (question: string) => void;
};

/** Shared owns the session, debounce, cancellation, keyboard selection, errors and rendering. */
export function QuickSearchDialog(props: QuickSearchDialogProps) {
  return props.open ? <SearchSession key={props.scopeKey} {...props} /> : null;
}

function SearchSession({ onClose, scopeKey, language, labels: overrides, pages, onPageOpen, pagePin, source, initialGroups, ask }: QuickSearchDialogProps) {
  const labels = { ...(language === LANGUAGE_DE ? searchTranslations.de : searchTranslations.en), ...overrides };
  const [term, setTerm] = useState("");
  const [delayed, setDelayed] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const id = useId();
  const trimmed = term.trim();
  useEffect(() => {
    const timer = window.setTimeout(() => setDelayed(trimmed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed]);
  const query = useQuery({
    queryKey: ["shared-quick-search", scopeKey, source?.key, delayed],
    queryFn: ({ signal }) => source!.load(delayed, signal),
    enabled: !!source && delayed.length >= MIN_SEARCH_LENGTH && delayed === trimmed,
    staleTime: SEARCH_STALE_TIME_MS,
    gcTime: 0,
    retry: false
  });
  const matchedPages = matchSearchPages(pages, trimmed);
  const groups: SearchGroup[] = [
    ...(!trimmed ? initialGroups ?? [] : delayed === trimmed ? query.data ?? [] : []),
    ...(matchedPages.length ? [{ key: "pages", label: labels.pages, entries: matchedPages.map(page => ({ key: `page:${page.path}`, label: page.label, description: page.context, open: () => onPageOpen(page), pin: pagePin?.(page) })) }] : [])
  ];
  const entries = groups.flatMap(group => group.entries);
  const question: SearchEntry | undefined = ask && trimmed.length >= MIN_SEARCH_LENGTH ? { key: QEDDY_ENTRY_KEY, label: labels.ask, description: `„${trimmed}“`, icon: <QeddyIcon size={QEDDY_ICON_SIZE} />, open: () => ask(trimmed) } : undefined;
  const all = question ? [...entries, question] : entries;
  const index = Math.min(activeIndex, Math.max(0, all.length - 1));
  useEffect(() => {
    document.getElementById(`${id}-${index}`)?.scrollIntoView({ block: "nearest" });
  }, [id, index]);
  const busy = !!source && trimmed.length >= MIN_SEARCH_LENGTH && (delayed !== trimmed || query.isFetching);
  const error = !!source && delayed === trimmed && trimmed.length >= MIN_SEARCH_LENGTH && query.isError;
  const select = (entry: SearchEntry) => { entry.open(); onClose(); };
  const renderEntry = (entry: SearchEntry, position: number) => <li key={entry.key} id={`${id}-${position}`} role="option" aria-selected={position === index}
    className={`mu-quick-search-row${entry.key === QEDDY_ENTRY_KEY ? " mu-quick-search-row--qeddy" : ""}${position === index ? " is-active" : ""}`} onMouseMove={() => setActiveIndex(position)}>
    <button type="button" className="mu-quick-search-open" onClick={() => select(entry)}>{entry.icon}<span className="mu-quick-search-text"><span className="mu-quick-search-title">{entry.label}</span>{entry.description ? <span className="mu-quick-search-sub">{entry.description}</span> : null}</span>{entry.key === QEDDY_ENTRY_KEY ? <ArrowRightOutlined className="mu-quick-search-go" aria-hidden /> : null}</button>
    {entry.pin ? <button type="button" className={`mu-quick-search-pin${entry.pin.active() ? " is-pinned" : ""}`} aria-label={entry.pin.active() ? labels.unpin : labels.pin} title={entry.pin.active() ? labels.unpin : labels.pin} onClick={entry.pin.toggle}>{entry.pin.active() ? <PushpinFilled /> : <PushpinOutlined />}</button> : null}
  </li>;
  let offset = 0;
  return <ThemedModal open onCancel={onClose} footer={null} closable={false} width={640} destroyOnHidden wrapClassName="mu-quick-search-wrap" title={null}>
    <div className="mu-quick-search" onKeyDown={event => {
      if (event.key === KEY_ESCAPE) { event.preventDefault(); onClose(); }
      else if (event.target instanceof HTMLElement && event.target.closest("button")) return;
      else if (event.key === KEY_ARROW_DOWN && all.length) { event.preventDefault(); setActiveIndex((index + 1) % all.length); }
      else if (event.key === KEY_ARROW_UP && all.length) { event.preventDefault(); setActiveIndex((index - 1 + all.length) % all.length); }
      else if (event.key === KEY_ENTER && all[index]) { event.preventDefault(); select(all[index]); }
    }}>
      <Input size="large" autoFocus allowClear prefix={<SearchOutlined />} placeholder={labels.placeholder} aria-label={labels.search}
        role="combobox" aria-expanded aria-controls={`${id}-results`} aria-activedescendant={all.length ? `${id}-${index}` : undefined} autoComplete="off" spellCheck={false} value={term} maxLength={SEARCH_MAX_LENGTH} onChange={event => { setTerm(event.target.value); setActiveIndex(0); }} />
      <div id={`${id}-results`} className="mu-quick-search-results" role="listbox" aria-label={labels.search} aria-busy={busy}>
        {groups.map(group => { const start = offset; offset += group.entries.length; return group.entries.length ? <section key={group.key}><Typography.Text type="secondary" className="mu-quick-search-heading">{group.label}</Typography.Text><ul>{group.entries.map((entry, i) => renderEntry(entry, start + i))}</ul></section> : null; })}
        {busy ? <Typography.Text role="status" type="secondary">{labels.loading}</Typography.Text> : null}
        {!busy && !error && trimmed.length >= MIN_SEARCH_LENGTH && entries.length === 0 ? <Typography.Text type="secondary" className="mu-quick-search-empty">{labels.empty}</Typography.Text> : null}
        {error ? <div role="alert"><Typography.Text type="danger">{labels.error}</Typography.Text><Button type="link" onClick={() => { return query.refetch(); }}>{labels.retry}</Button></div> : null}
        {question ? <section className="mu-quick-search-qeddy"><ul>{renderEntry(question, entries.length)}</ul></section> : null}
      </div>
    </div>
  </ThemedModal>;
}
